import { defineStore } from 'pinia';
import { DossiersResponse, Dossier, DossiersTotal, DossiersTotalResponse, DossierResponse } from '@graphql/types/dossiers';
import { GET_DOSSIER, GET_DOSSIERS, GET_TOTALS, GET_DOSSIER_TITLE, GET_DOSSIER_ID } from '@graphql/queries/dossiers';
import { CombinedError, OperationResult, AnyVariables } from '@urql/vue';
import { QueryDossiersOrderByColumn, SortOrder } from '@graphql/enums/dossiers';
import { useUrqlStore } from '@stores/useUrqlStore';

import type { DropDownOption } from '@graphql/types';

export const storeSetup = () => {
  const { gqlClient } = toRefs(useUrqlStore());

  // state
  const paginatorTotal = ref<number>(0);
  const perPage = ref<number>(10);
  const dossiers = ref<Dossier[]>([]);
  const searchedDossiers = ref<Dossier[]>([]);
  const dossiersTotals = ref<DossiersTotal>();
  const searchTerm = ref<string>('');
  const page = ref<number>(0);
  const selectedStatusFilter = ref({
    label: 'Alle aanvragen',
    value: '',
  });
  const selectedOrderDate = ref<UIDropDownOption>({
    label: 'Nieuwste eerst',
    value: SortOrder.DESC,
  });

  const input = computed(() => {
    if (selectedStatusFilter.value.value) {
      return { search_term: searchTerm.value, state: selectedStatusFilter.value.value };
    }
    return { search_term: searchTerm.value };
  });

  // actions
  const fetchDossiers = async (limit: number): Promise<DossiersResponse | undefined> => {
    const fetchDossiersInput = {
      input: input.value,
      orderBy: [{ column: QueryDossiersOrderByColumn.CREATED_AT, order: selectedOrderDate.value.value }],
      limit,
      page: page.value,
    };
    return gqlClient.value
      .query<DossiersResponse>(GET_DOSSIERS, fetchDossiersInput)
      .toPromise()
      .then(result => {
        if (result.data) {
          paginatorTotal.value = result.data?.dossiers.paginatorInfo.total ?? 0;
          perPage.value = result.data.dossiers.paginatorInfo.perPage ?? 10;
          return result.data;
        }

        if (result.error) {
          throw new CombinedError(result.error);
        }

        return undefined;
      });
  };

  const fetchDossier = async (id: number|string): Promise<Dossier | undefined> => {
    return gqlClient.value
      .query<DossierResponse>(GET_DOSSIER, { id })
      .toPromise()
      .then(result => {
        if (result.data) {
          return result.data.dossier;
        }

        if (result.error) {
          throw new CombinedError(result.error);
        }

        return undefined;
      });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fetchDossierTitle = async (id: string): Promise<any> => {
    return gqlClient.value
      .query<DossiersResponse>(GET_DOSSIER_TITLE, { id })
      .toPromise()
      .then(result => {
        return result;
      });
  };

  const fetchDossierId = async (id: string): Promise<OperationResult<DossiersResponse, AnyVariables>> => {
    return gqlClient.value
      .query<DossiersResponse>(GET_DOSSIER_ID, { id })
      .toPromise()
      .then(result => {
        return result;
      });
  };


  const setDossiers = async (limit = 10): Promise<CombinedError | void> => {
    const fetchedDossiers = await fetchDossiers(limit);

    dossiers.value = fetchedDossiers?.dossiers.data;
    searchedDossiers.value = fetchedDossiers?.dossiers.data;
    paginatorTotal.value = fetchedDossiers?.dossiers.paginatorInfo.total;
  };

  const setMoreDossiers = async (limit = 10): Promise<CombinedError|void> => {
    const fetchedDossiers = await fetchDossiers(limit);

    dossiers.value.push(...fetchedDossiers?.dossiers.data);
  };

  const setSearchedTerm = async (search: string): Promise<CombinedError|void> => {
    searchTerm.value = search;
  };

  const setStatusFilter = async (option: DropDownOption) => {
    selectedStatusFilter.value = option;
  };

  const setOrderFilter = async (option: DropDownOption) => {
    selectedOrderDate.value = option;
  };

  const resetPage = () => {
    page.value = 1;
  };

  const setTotals = async (newDossiersRangeDays: string, loanRangeDays: string): Promise<DossiersTotal> => {
    return new Promise((_resolve, _reject) => {
      gqlClient.value
        .query<DossiersTotalResponse>(GET_TOTALS, {
          newDossiersRangeDays,
          loanRangeDays,
        })
        .toPromise()
        .then(result => {
          if (result.error) {
            throw new CombinedError(result.error);
          }

          if (result.data) {
            dossiersTotals.value = result.data.totals;
          }

          return result.data;
        });
    });
  };

  const replaceDossier = (dossier: Dossier) => {
    const indexDossiers = dossiers.value.findIndex(doss => doss.id === dossier.id);
    const indexSearched = searchedDossiers.value.findIndex(doss => doss.id === dossier.id);

    if (indexDossiers !== -1) {
      dossiers.value.splice(indexDossiers, 1, dossier);
    }

    if (indexSearched !== -1) {
      searchedDossiers.value.splice(indexDossiers, 1, dossier);
    }
  };

  return {
    // state
    paginatorTotal,
    perPage,
    dossiers,
    searchedDossiers,
    dossiersTotals,
    searchTerm,
    page,
    selectedStatusFilter,
    selectedOrderDate,
    gqlClient,

    // setters
    setDossiers,
    setTotals,
    setMoreDossiers,
    setSearchedTerm,
    setStatusFilter,
    setOrderFilter,
    resetPage,
    replaceDossier,

    // actions
    fetchDossier,
    fetchDossierTitle,
    fetchDossierId,
  };
};

export const useDossiersStore = defineStore('dossier', storeSetup, { persistedState: { persist: false } });
